// REST client for the server's replay read path. The server folds the
// archived bundle through the requested perspective's projection and returns
// { base, frames[], meta, ownSeat }; nothing unprojected ever reaches us.
import { api } from '../../serverClient';

export const REPLAY_PERSPECTIVES = ['p1', 'p2', 'spectator', 'omniscient'];

// `link` is a public replay-link token; it rides every call as `?link=` and is
// the whole credential for viewers who are not participants (or not logged in).
function linkQuery(link, first = true) {
  return link ? `${first ? '?' : '&'}link=${encodeURIComponent(link)}` : '';
}

export async function fetchReplay(roomCode, perspective = null, link = null) {
  const query = perspective ? `?perspective=${encodeURIComponent(perspective)}` : '';
  return api.get(`/matches/${encodeURIComponent(roomCode)}/replay${query}${linkQuery(link, !query)}`);
}

export async function createReplayLink(roomCode, link = null) {
  return api.post(`/matches/${encodeURIComponent(roomCode)}/replay/link${linkQuery(link)}`, {});
}

export async function resolveReplayLink(token) {
  return api.get(`/matches/shared/${encodeURIComponent(token)}`);
}

// Per-row comment threads on the replay feed. Keys are "<seq>:<entryIndex>"
// / "<seq>:turn". The read returns every row's thread in one shot: the
// overlay opens a thread on hover, far too hot a path to fetch per row.
export async function fetchReplayComments(roomCode, link = null) {
  return api.get(`/matches/${encodeURIComponent(roomCode)}/replay/comments${linkQuery(link)}`);
}

export async function postReplayComment(roomCode, key, body, link = null) {
  return api.post(`/matches/${encodeURIComponent(roomCode)}/replay/comments${linkQuery(link)}`, { key, body });
}

export async function deleteReplayComment(roomCode, commentId) {
  return api.delete(`/matches/${encodeURIComponent(roomCode)}/replay/comments/${encodeURIComponent(commentId)}`);
}

export async function toggleReplayCommentLike(roomCode, commentId, link = null) {
  return api.post(`/matches/${encodeURIComponent(roomCode)}/replay/comments/${encodeURIComponent(commentId)}/like${linkQuery(link)}`, {});
}

// Rename / pin one of the caller's own matches (their match_history row).
export async function updateMatchMeta(roomCode, { name, pinned } = {}) {
  return api.put(`/matches/${encodeURIComponent(roomCode)}/history`, { name, pinned });
}

// The matchup only: both seats' Avatar cards, without the frames. Used by the
// chat share card, which must render a solo match as the two decks the pilot
// sat behind (match_history writes one row and no avatar for those).
export async function fetchReplaySummary(roomCode, link = null) {
  return api.get(`/matches/${encodeURIComponent(roomCode)}/replay/summary${linkQuery(link)}`);
}
